import React from 'react'
import { router, usePage } from '@inertiajs/react'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface LeadForm3Props {
  brand: string
  /** when present, the neutral demo confirmation renders INSTEAD of the form */
  submitted: 'demo' | null
}

const NAME_STORAGE_KEY = 'mkt_lead_name'

function storedFirstName(): string | null {
  try {
    const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem(NAME_STORAGE_KEY) : null
    if (!raw) return null
    const first = raw.trim().split(/\s+/)[0]
    return first || null
  } catch {
    return null
  }
}

const fieldBase =
  'h-11 rounded-lg border-stone-200 bg-white text-[15px] focus-visible:ring-teal-600 focus-visible:ring-offset-0'

/**
 * LeadForm3 — the one lead form for Landing3. ONE path: "Book a demo". Fields are
 * Name* · Email* · Property or company name · a FREE-TEXT "Which PMS do you use?"
 * (no PMS name ever appears) · an optional message, plus the honeypot. It POSTs via
 * Inertia to /demo-request; the server redirects back to root with ?submitted=demo#demo
 * — at which point the parent re-renders this with `submitted` set and the neutral demo
 * confirmation replaces the form.
 *
 * The old two-path lead toggle was collapsed to this single "Book a demo" path
 * 2026-07-05 (founder direction — see copy doc §Final CTA + form): a visitor who only
 * wants a price is covered by the pricing-reassurance line in the sub. The form no longer
 * sends the intent field; the leads.intent data column is untouched and defaults to 'demo'
 * server-side.
 *
 * Failures arrive as an `alert` flash via Inertia shared props and render inline above the
 * form — this page never uses the operator toast system. All copy is VERBATIM from the
 * copy doc's §Final CTA + form.
 */
export default function LeadForm3({ brand, submitted }: LeadForm3Props) {
  const { props } = usePage<{ flash?: { alert?: string } }>()
  const flashAlert = props.flash?.alert

  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [company, setCompany] = React.useState('')
  const [pms, setPms] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [website, setWebsite] = React.useState('') // honeypot
  const [submitting, setSubmitting] = React.useState(false)

  if (submitted) {
    const first = storedFirstName()
    const thanks = first ? `Thanks, ${first}` : 'Thanks'
    return (
      <div
        className="rounded-2xl border border-teal-600/20 bg-teal-50/60 p-6 sm:p-8"
        role="status"
        aria-live="polite"
        data-testid="lead-confirmation"
      >
        <CheckCircle2 className="h-8 w-8 text-teal-600" aria-hidden="true" />
        <p className="mkt3-prose mt-4 text-slate-700">
          {thanks} — we’ll be in touch shortly to arrange your demo.
        </p>
      </div>
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    try {
      window.sessionStorage.setItem(NAME_STORAGE_KEY, name)
    } catch {
      /* private mode — confirmation falls back to the nameless variant */
    }
    setSubmitting(true)
    router.post(
      '/demo-request',
      {
        name,
        email,
        company,
        pms,
        notes,
        website,
      },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <form onSubmit={handleSubmit} className="text-left">
      {flashAlert && (
        <div
          role="alert"
          className="mb-5 flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{flashAlert}</span>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="lead3-name" className="text-slate-700">
            Your name
          </Label>
          <Input
            id="lead3-name"
            name="name"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldBase}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lead3-email" className="text-slate-700">
            Email
          </Label>
          <Input
            id="lead3-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={fieldBase}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lead3-company" className="text-slate-700">
            Property or company name{' '}
            <span className="font-normal text-slate-400">— optional</span>
          </Label>
          <Input
            id="lead3-company"
            name="company"
            autoComplete="organization"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className={fieldBase}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lead3-pms" className="text-slate-700">
            Which PMS do you use?{' '}
            <span className="font-normal text-slate-400">— optional</span>
          </Label>
          <Input
            id="lead3-pms"
            name="pms"
            autoComplete="off"
            value={pms}
            onChange={(e) => setPms(e.target.value)}
            className={fieldBase}
          />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="lead3-notes" className="text-slate-700">
            Anything you’d like us to know?{' '}
            <span className="font-normal text-slate-400">— optional</span>
          </Label>
          <textarea
            id="lead3-notes"
            name="notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="flex w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-[15px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>

      {/* Honeypot — genuinely invisible to humans, present for bots */}
      <div className="mkt3-hp" aria-hidden="true">
        <label htmlFor="lead3-website">Website</label>
        <input
          id="lead3-website"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mkt3-cta mt-6 inline-flex h-12 w-full items-center justify-center rounded-lg bg-teal-600 px-8 text-[15px] font-semibold text-white hover:bg-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 sm:w-auto"
      >
        {submitting ? 'Sending…' : 'Book a demo'}
      </button>

      <p className="mt-4 text-xs leading-relaxed text-slate-500">
        We’ll only use your details to reply about {brand}. No mailing lists, no sharing, ever.
      </p>
    </form>
  )
}
